import type { GameSummary } from '@brig/api-types';
import { button, caps, paper } from '@brig/theme/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMe, useMyGames } from '../api/hooks.ts';
import { LoadingNotice } from '../components/LoadingNotice.tsx';
import { NewGameButton } from '../components/NewGameButton.tsx';
import { ShipDraughts } from '../components/ShipDraughts.tsx';
import { useDocumentTitle } from '../useDocumentTitle.ts';
import * as styles from './Harbour.css.ts';
import { nextOrder, orderOfTheDay, rankOf, shipsBell } from './harbour/watch.ts';
import { OpponentName } from './Logbook.tsx';

/** The time, kept to the minute. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return now;
}

/**
 * The captain's home port, first after signing in: hailed by rank with the watch and the
 * bells, the game that waits for their orders, their service record, and the admiral's order
 * of the day, over the pencil draughts of a brig. The full log of games is a click away.
 */
export function Harbour() {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t('harbour.pageTitle'));
  const { data: me } = useMe();
  const games = useMyGames();
  const now = useNow();
  const { watch, bells } = shipsBell(now);
  const items = games.data?.items ?? [];
  const concluded = items.filter((g) => g.status === 'finished').length;
  const { rank } = rankOf(concluded);
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'full' }).format(now);

  return (
    <div className={styles.page}>
      <ShipDraughts className={styles.draughts} />
      <header className={styles.hail}>
        <p className={`${caps} ${styles.watch}`}>
          {t(`harbour.watches.${watch}`)} · {t(`harbour.bells.b${bells}`)}
        </p>
        <h1 className={styles.title}>
          {me
            ? t('harbour.welcome', { rank: t(`harbour.ranks.${rank.id}`), name: me.nickname })
            : t('harbour.title')}
        </h1>
        <p className={styles.date}>{date}</p>
      </header>

      {games.isPending ? <LoadingNotice /> : null}
      {games.isError ? (
        <section className={paper} role="alert">
          <p>{t('profile.loadFailed')}</p>
          <button type="button" className={button({ tone: 'label' })} onClick={() => void games.refetch()}>
            {t('profile.retry')}
          </button>
        </section>
      ) : null}
      {games.data ? (
        <div className={styles.grid}>
          <Helm games={items} />
          <ServiceRecord games={items} />
          <OrderOfTheDay now={now} />
        </div>
      ) : null}
    </div>
  );
}

/** The game that waits for the captain's orders, or the one they wait on, or a calm. */
function Helm({ games }: { games: readonly GameSummary[] }) {
  const { t, i18n } = useTranslation();
  const live = games.filter((g) => g.status === 'active' || g.status === 'pending');
  const mine = live.filter((g) => g.turn === 'mine');
  const game = mine[0] ?? live[0];
  if (!game) {
    return (
      <section className={`${paper} ${styles.helm}`} aria-labelledby="helm-title">
        <p className={`${caps} ${styles.kicker}`}>{t('harbour.helm.kicker')}</p>
        <h2 id="helm-title" className={styles.helmTitle}>
          {t('harbour.helm.calmTitle')}
        </h2>
        <p className={styles.helmBody}>{t('harbour.helm.calmBody')}</p>
        <div className={styles.helmActions}>
          <NewGameButton />
        </div>
      </section>
    );
  }
  const yours = game.turn === 'mine';
  const ago = relative(game.updatedAt, i18n.language);
  return (
    <section className={`${paper} ${styles.helm}`} aria-labelledby="helm-title">
      <p className={`${caps} ${styles.kicker}`}>{t('harbour.helm.kicker')}</p>
      <h2 id="helm-title" className={styles.helmTitle}>
        {yours ? t('harbour.helm.yourMove') : t('harbour.helm.theirMove')}
      </h2>
      <p className={styles.helmGame}>
        <OpponentName game={game} />
        {game.turnNumber > 0 ? (
          <span className={styles.turn}>{t('harbour.helm.turn', { n: game.turnNumber })}</span>
        ) : null}
      </p>
      <p className={styles.helmBody}>{t('harbour.helm.lastEntry', { when: ago })}</p>
      <div className={styles.helmActions}>
        <Link to={`/games/${game.id}`} className={button({ tone: yours ? 'seal' : 'label', size: 'lg' })}>
          {yours ? t('harbour.helm.take') : t('harbour.helm.look')}
        </Link>
        {mine.length > 1 ? (
          <Link to="/logbook" className={styles.more}>
            {t('harbour.helm.more', { n: mine.length - 1 })}
          </Link>
        ) : null}
      </div>
    </section>
  );
}

/** Voyages, those under way and those concluded, the rank they earn, and the full log. */
function ServiceRecord({ games }: { games: readonly GameSummary[] }) {
  const { t } = useTranslation();
  const underWay = games.filter((g) => g.status === 'active' || g.status === 'pending').length;
  const concluded = games.filter((g) => g.status === 'finished').length;
  const { rank, next } = rankOf(concluded);
  const figures = [
    ['voyages', games.length],
    ['underWay', underWay],
    ['concluded', concluded],
  ] as const;
  return (
    <section className={`${paper} ${styles.record}`} aria-labelledby="record-title">
      <h2 id="record-title" className={`${caps} ${styles.kicker}`}>
        {t('harbour.record.title')}
      </h2>
      <dl className={styles.figures}>
        {figures.map(([key, n]) => (
          <div key={key} className={styles.figure}>
            <dt className={styles.figureLabel}>{t(`harbour.record.${key}`)}</dt>
            <dd className={styles.figureValue}>{n}</dd>
          </div>
        ))}
      </dl>
      <p className={styles.rank}>
        {t('harbour.record.rank')}: <strong>{t(`harbour.ranks.${rank.id}`)}</strong>
      </p>
      {next ? (
        <p className={styles.next}>
          {t('harbour.record.next', { rank: t(`harbour.ranks.${next.id}`), n: next.from })}
        </p>
      ) : null}
      <Link to="/logbook" className={button({ tone: 'label' })}>
        {t('harbour.record.openLog')}
      </Link>
    </section>
  );
}

/** One of the admiral's standing orders, a new one each day. */
function OrderOfTheDay({ now }: { now: Date }) {
  const { t } = useTranslation();
  const [n, setN] = useState(() => orderOfTheDay(now));
  return (
    <section className={`${paper} ${styles.order}`} aria-labelledby="order-title">
      <h2 id="order-title" className={`${caps} ${styles.kicker}`}>
        {t('harbour.order.title')}
      </h2>
      <blockquote className={styles.orderText}>{t(`harbour.orders.o${n}`)}</blockquote>
      <p className={styles.orderSign}>— {t('harbour.order.signed')}</p>
      <button
        type="button"
        className={`${button({ tone: 'ghost', size: 'sm' })} ${styles.nextOrder}`}
        onClick={() => {
          setN(nextOrder);
        }}
      >
        {t('harbour.order.next')} →
      </button>
    </section>
  );
}

/** "5 minutes ago", "yesterday": how long since `iso`, in the reader's language. */
function relative(iso: string, locale: string): string {
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  const f = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const steps: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [3600, 'minute'],
    [86_400, 'hour'],
    [604_800, 'day'],
    [2_629_800, 'week'],
    [31_557_600, 'month'],
  ];
  const units: Record<string, number> = {
    second: 1,
    minute: 60,
    hour: 3600,
    day: 86_400,
    week: 604_800,
    month: 2_629_800,
  };
  for (const [limit, unit] of steps) {
    if (Math.abs(s) < limit) return f.format(Math.round(s / (units[unit] ?? 1)), unit);
  }
  return f.format(Math.round(s / 31_557_600), 'year');
}
